import type { Action } from 'redux';

import normaliseError, { type NormaliseErrorInput } from '/shared/functions/normaliseError';
import type { NormalisedError } from '/index';
import type { GetApiB2bApplicationByIdFormStateResponses } from '/api';
import type { InitiativeStatus } from '/business/shared/state/actions/basket/updateInitiativeStatus';

type FormStateResponse = GetApiB2bApplicationByIdFormStateResponses[200];
type FullFormStateResponse = Extract<FormStateResponse, { state: { items: object[] } }>;
type PartialFormStateResponse = Extract<FormStateResponse, { revalidatedFor: string[] }>;
type MetaPartialFormStateResponse = Extract<FormStateResponse, { revalidatedForMeta: string[] }>;
type FormStateBlob = FullFormStateResponse['state'];

export type BasketItem = FormStateBlob['items'][number];
export type BasketMeta = FormStateBlob['meta'] & {
  createdInitiative?: null;
};
export type StructuredAnswer = FullFormStateResponse['questionAnswerStructured'][number];
export type AttachedInitiative = NonNullable<BasketMeta['reductionInitiatives']>[number];
export type DeletedInitiative = NonNullable<BasketMeta['reductionInitiativesDeletedTracker']>[number];
export type RequestedInitiativeInformation = NonNullable<BasketMeta['reductionInitiativesRequestedInfo']>[number];

export interface BasketState {
  items: BasketItem[];
  questionAnswerStructured: StructuredAnswer[];
  meta: BasketMeta;
  adding: boolean;
  added: boolean;
  errors: Array<NormalisedError> | null;
  replaced: boolean;
  replacing: boolean;
  set: boolean;
  stateAppId: number | null;
  lastRetrievedAt: string | null;
  saving: boolean;
  metaSaveAttempting: boolean;
  metaSaveAttemptingSuccessful: boolean;
  error?: NormalisedError | null;
}

interface BskSaveAttemptBegunAction extends Action {
  type: 'BSK_SAVE_ATTEMPT_BEGUN';
}

interface BskSaveAttemptCompleteAction extends Action {
  type: 'BSK_SAVE_ATTEMPT_COMPLETE';
}

interface BskSaveAttemptReqConfirmAction extends Action {
  type: 'BSK_SAVE_ATTEMPT_REQ_CONFIRM';
}

interface BskSaveAttemptRejectedAction extends Action {
  type: 'BSK_SAVE_ATTEMPT_REJECTED';
  error: NormaliseErrorInput;
}

interface BasketAddStartedAction extends Action {
  type: 'BASKET_ADD_STARTED';
}

interface BasketAddRejectedAction extends Action {
  type: 'BASKET_ADD_REJECTED';
  error: NormaliseErrorInput;
}

interface BasketAddFulfilledAction extends Action {
  type: 'BASKET_ADD_FULFILLED';
}

interface BasketUpdateFulfilledAction extends Action {
  type: 'BASKET_UPDATE_FULFILLED';
}

interface UpdateInitiativeStatusFulfilledAction extends Action {
  type: 'UPDATE_INITIATIVE_STATUS_FULFILLED';
  attachmentId: string | number;
  status: InitiativeStatus;
}

interface MetaChangeRejectedAction extends Action {
  type: 'META_CHANGE_REJECTED';
  error: NormaliseErrorInput;
}

interface BasketAddMultipleStartedAction extends Action {
  type: 'BASKET_ADD_MULTIPLE_STARTED';
}

interface BasketAddMultipleRejectedAction extends Action {
  type: 'BASKET_ADD_MULTIPLE_REJECTED';
  error: NormaliseErrorInput;
}

interface BasketUpdateStartedAction extends Action {
  type: 'BASKET_UPDATE_STARTED';
}

interface BasketReplaceMetaPartialFulfilledAction extends Action {
  type: 'BASKET_REPLACE_META_PARTIAL_FULFILLED';
  revalidatedState: MetaPartialFormStateResponse['revalidatedState'];
  applicationId: number;
  revalidatedForMeta: Array<string>;
  retrievedAt: string;
}

interface BasketReplacePartialFulfilledAction extends Action {
  type: 'BASKET_REPLACE_PARTIAL_FULFILLED';
  revalidatedState: PartialFormStateResponse['revalidatedState'];
  applicationId: number;
  revalidatedQuestionAnswerStructured: PartialFormStateResponse['revalidatedQuestionAnswerStructured'];
  retrievedAt: string;
  revalidatedFor: Array<string>;
}

interface BasketReplaceFulfilledAction extends Action {
  type: 'BASKET_REPLACE_FULFILLED';
  items: FormStateBlob['items'];
  meta: BasketMeta;
  stateAppId: number;
  questionAnswerStructured: FullFormStateResponse['questionAnswerStructured'];
  retrievedAt: string;
}

interface BasketUpdateRejectedAction extends Action {
  type: 'BASKET_UPDATE_REJECTED';
  error: NormaliseErrorInput;
}

interface BasketReplaceStartedAction extends Action {
  type: 'BASKET_REPLACE_STARTED';
}

interface BasketReplaceRejectedAction extends Action {
  type: 'BASKET_REPLACE_REJECTED';
}

interface SetApplicationActiveFulfilledAction extends Action {
  type: 'SET_APPLICATION_ACTIVE_FULFILLED';
  applicationId?: number;
  cachedBasket?: Partial<BasketState>;
}

interface ClearAuthenticationAction extends Action {
  type: 'CLEAR_AUTHENTICATION';
}

type BasketAction =
  | BskSaveAttemptBegunAction
  | BskSaveAttemptCompleteAction
  | BskSaveAttemptReqConfirmAction
  | BskSaveAttemptRejectedAction
  | BasketAddStartedAction
  | BasketAddRejectedAction
  | BasketAddFulfilledAction
  | BasketUpdateFulfilledAction
  | UpdateInitiativeStatusFulfilledAction
  | MetaChangeRejectedAction
  | BasketAddMultipleStartedAction
  | BasketAddMultipleRejectedAction
  | BasketUpdateStartedAction
  | BasketReplaceMetaPartialFulfilledAction
  | BasketReplacePartialFulfilledAction
  | BasketReplaceFulfilledAction
  | BasketUpdateRejectedAction
  | BasketReplaceStartedAction
  | BasketReplaceRejectedAction
  | SetApplicationActiveFulfilledAction
  | ClearAuthenticationAction;

const defaultState: BasketState = {
  items: [],
  questionAnswerStructured: [],
  meta: {},
  adding: false,
  added: false,
  errors: null,
  replaced: false,
  replacing: false,
  set: false,
  stateAppId: null,
  lastRetrievedAt: null,
  saving: false,
  metaSaveAttempting: false,
  metaSaveAttemptingSuccessful: false,
};

function reducer(state: BasketState = defaultState, action: BasketAction): BasketState {
  switch (action.type) {
    case 'BSK_SAVE_ATTEMPT_BEGUN': {
      return {
        ...state,
        saving: true,
        metaSaveAttempting: true,
        metaSaveAttemptingSuccessful: false,
      };
    }
    case 'BSK_SAVE_ATTEMPT_COMPLETE': {
      return {
        ...state,
        saving: false,
        metaSaveAttempting: false,
        metaSaveAttemptingSuccessful: true,
      };
    }
    case 'BSK_SAVE_ATTEMPT_REQ_CONFIRM': {
      return {
        ...state,
        saving: false,
        metaSaveAttempting: false,
      };
    }
    case 'BSK_SAVE_ATTEMPT_REJECTED': {
      return {
        ...state,
        saving: false,
        metaSaveAttempting: false,
        errors: [...(state.errors || []), normaliseError(action.error)],
      };
    }
    case 'BASKET_ADD_STARTED': {
      return {
        ...state,
        adding: true,
        errors: null,
      };
    }

    case 'BASKET_ADD_REJECTED': {
      return {
        ...state,
        adding: false,
        errors: [...(state.errors || []), normaliseError(action.error)],
      };
    }
    case 'BASKET_ADD_FULFILLED':
    case 'BASKET_UPDATE_FULFILLED': {
      return {
        ...state,
        adding: false,
      };
    }
    case 'UPDATE_INITIATIVE_STATUS_FULFILLED': {
      const { attachmentId, status } = action;
      return {
        ...state,
        meta: {
          ...state.meta,
          reductionInitiatives: (state.meta.reductionInitiatives || []).map((initiative) => {
            if (initiative.itemId === attachmentId) {
              return { ...initiative, status };
            }
            return initiative;
          }),
        },
      };
    }
    case 'META_CHANGE_REJECTED': {
      return {
        ...state,
        errors: [...(state.errors || []), normaliseError(action.error)],
      };
    }
    case 'BASKET_ADD_MULTIPLE_STARTED': {
      return {
        ...state,
        adding: true,
        errors: null,
      };
    }
    case 'BASKET_ADD_MULTIPLE_REJECTED': {
      return {
        ...state,
        adding: false,
        errors: [...(state.errors || []), normaliseError(action.error)],
      };
    }
    case 'BASKET_UPDATE_STARTED': {
      return {
        ...state,
        adding: true,
        error: null,
      };
    }
    case 'BASKET_REPLACE_META_PARTIAL_FULFILLED': {
      const { revalidatedState, applicationId, revalidatedForMeta, retrievedAt } = action;
      const oldMeta = { ...state.meta };
      const newMeta: Partial<BasketMeta> = {};
      if (
        revalidatedForMeta.includes('partial_load_request_initiative_info') ||
        revalidatedForMeta.includes('partial_unload_request_initiative_info')
      ) {
        newMeta.reductionInitiativesRequestedInfo = revalidatedState.meta.reductionInitiativesRequestedInfo;
      }

      return {
        ...state,
        meta: {
          ...oldMeta,
          ...newMeta,
        },
        replaced: true,
        adding: false,
        set: false,
        replacing: false,
        stateAppId: applicationId,
        lastRetrievedAt: retrievedAt,
      };
    }
    case 'BASKET_REPLACE_PARTIAL_FULFILLED': {
      const { revalidatedState, applicationId, revalidatedQuestionAnswerStructured, retrievedAt, revalidatedFor } =
        action;

      const replacedItems = state.items.filter((item) => {
        if (item.type && ['neo-branch-supplier', 'neo-branch-custom'].includes(item.type)) {
          if (item.genericData?.base_question_key && revalidatedFor.includes(item.genericData.base_question_key)) {
            return false;
          }
        }
        if (item.type === 'calculation-fuel-and-energy-related') {
          return !revalidatedState.some(
            (revalidatedItem) =>
              revalidatedItem.type === 'calculation-fuel-and-energy-related' &&
              revalidatedItem.itemId === item.itemId,
          );
        }
        return !revalidatedFor.includes(item.type as string);
      });

      const replacedStructured = [...state.questionAnswerStructured];

      revalidatedFor.forEach((keyToReplace: string) => {
        const replaceKey = replacedStructured.findIndex((needle) => {
          return keyToReplace === needle.key;
        });

        const replacement = revalidatedQuestionAnswerStructured.find((needle) => {
          return keyToReplace === needle.key;
        });
        if (!replacement) {
          console.warn('Revalidation key not found:', keyToReplace);
        } else if (replaceKey === -1) {
          replacedStructured.push(replacement);
        } else {
          replacedStructured[replaceKey] = replacement;
        }
      });

      return {
        ...state,
        items: replacedItems.concat(revalidatedState),
        questionAnswerStructured: replacedStructured,

        replaced: true,
        adding: false,
        set: false,
        replacing: false,
        stateAppId: applicationId,
        lastRetrievedAt: retrievedAt,
      };
    }
    case 'BASKET_REPLACE_FULFILLED': {
      const { items, meta, stateAppId, questionAnswerStructured, retrievedAt } = action;
      return {
        ...state,
        items,
        questionAnswerStructured,
        meta: {
          ...meta,
          createdInitiative: null,
        },
        replaced: true,
        adding: false,
        set: false,
        replacing: false,
        stateAppId,
        lastRetrievedAt: retrievedAt,
      };
    }

    case 'BASKET_UPDATE_REJECTED': {
      return {
        ...state,
        adding: false,
        error: normaliseError(action.error),
      };
    }

    case 'BASKET_REPLACE_STARTED': {
      return {
        ...state,
        replacing: true,
        set: false,
      };
    }

    case 'BASKET_REPLACE_REJECTED': {
      return {
        ...state,
        meta: {},
        questionAnswerStructured: [],
        replaced: true,
        adding: false,
        set: false,
        replacing: false,
        lastRetrievedAt: null,
        stateAppId: 0,
      };
    }

    case 'SET_APPLICATION_ACTIVE_FULFILLED': {
      if (!action.applicationId || action.applicationId === state.stateAppId) {
        return state;
      }
      if (action.cachedBasket) {
        return {
          ...state,
          ...action.cachedBasket,
          stateAppId: action.applicationId,
          replaced: true,
          replacing: false,
          saving: false,
        };
      }
      return {
        ...defaultState,
        stateAppId: action.applicationId,
      };
    }
    case 'CLEAR_AUTHENTICATION': {
      return {
        ...defaultState,
      };
    }
    default: {
      return state;
    }
  }
}

export default reducer;
